import React, { useEffect, useState } from "react";
import { getBearerHeaders } from "../../helpers/GetBearerHeaders";
import { GetAdminDashboard } from "../../api-requests/Get";
import type {
  AdminDashboardData,
  ArtistCount,
  TrendingArtist,
} from "../../types/adminDashboard";

// ─── Tiny UI primitives ────────────────────────────────────────────────────

const StatCard = ({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: string;
}) => (
  <div className="bg-gray-800 rounded-lg px-4 py-5">
    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
      {label}
    </p>
    <p className={`mt-1 text-3xl font-bold ${accent ?? "text-white"}`}>
      {value}
    </p>
    {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
  </div>
);

const PercentBar = ({
  label,
  count,
  percent,
  color = "bg-indigo-500",
}: {
  label: string;
  count: number;
  percent: number;
  color?: string;
}) => (
  <div>
    <div className="flex justify-between text-xs mb-1">
      <span className="text-gray-200 truncate max-w-[70%]">{label}</span>
      <span className="text-gray-400">
        {percent}% <span className="text-gray-600">({count})</span>
      </span>
    </div>
    <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
      <div
        className={`h-1.5 rounded-full ${color}`}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  </div>
);

const SectionHeader = ({ title }: { title: string }) => (
  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-3">
    {title}
  </h3>
);

const SkeletonCard = () => (
  <div className="bg-gray-800 rounded-lg px-4 py-5 animate-pulse">
    <div className="h-3 bg-gray-700 rounded w-1/2 mb-3" />
    <div className="h-8 bg-gray-700 rounded w-1/3" />
  </div>
);

const formatUptime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

// ─── Mini trend sparkline (CSS-only, no chart lib needed) ─────────────────

const MiniSparkline = ({
  data,
}: {
  data: Array<{ date: string; count: number }>;
}) => {
  if (!data || data.length === 0)
    return <span className="text-gray-600 text-xs">no data</span>;
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-0.5 h-8">
      {data.map((d, i) => (
        <div
          key={i}
          title={`${d.date}: ${d.count}`}
          className="flex-1 bg-indigo-500 rounded-sm opacity-75 hover:opacity-100 transition-opacity"
          style={{ height: `${Math.max(4, (d.count / max) * 32)}px` }}
        />
      ))}
    </div>
  );
};

// ─── Artist table ─────────────────────────────────────────────────────────

const ArtistTable = ({
  rows,
  showDelta,
}: {
  rows: ArtistCount[] | TrendingArtist[];
  showDelta?: boolean;
}) => (
  <table className="w-full text-sm">
    <tbody>
      {rows.map((row, i) => {
        const isTrending = showDelta && "delta" in row;
        const delta = isTrending ? (row as TrendingArtist).delta : null;
        return (
          <tr
            key={row.itemId}
            className="border-b border-gray-700 last:border-0"
          >
            <td className="py-1.5 pr-2 text-gray-500 w-6 text-xs">{i + 1}</td>
            <td className="py-1.5 text-gray-100 flex-1">{row.name}</td>
            <td className="py-1.5 pl-2 text-right w-20">
              {isTrending ? (
                <span
                  className={
                    delta! > 0
                      ? "text-green-400"
                      : delta! < 0
                        ? "text-red-400"
                        : "text-gray-400"
                  }
                >
                  {delta! > 0 ? "+" : ""}
                  {delta}
                  <span className="text-gray-600 text-xs ml-1">
                    ({(row as TrendingArtist).recent})
                  </span>
                </span>
              ) : (
                <span className="text-gray-300">
                  {(row as ArtistCount).count}
                </span>
              )}
            </td>
          </tr>
        );
      })}
      {rows.length === 0 && (
        <tr>
          <td colSpan={3} className="py-3 text-gray-600 text-xs text-center">
            No data yet
          </td>
        </tr>
      )}
    </tbody>
  </table>
);

// ─── Main component ────────────────────────────────────────────────────────

const DashboardAdmin = () => {
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await GetAdminDashboard(getBearerHeaders());
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.error || "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-400 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { growth, engagement, productInsights, tasteInsights, social, system } =
    data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-10 pb-12">
      {/* ── Top KPI Row ─────────────────────────────────────────────────── */}
      <div>
        <SectionHeader title="Growth" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard
            label="Total Users"
            value={growth.totalUsers}
            accent="text-white"
          />
          <StatCard
            label="New Today"
            value={growth.newUsersToday}
            accent="text-green-400"
          />
          <StatCard
            label="New (7d)"
            value={growth.newUsersLast7Days}
            accent="text-green-300"
          />
          <StatCard
            label="Active (24h)"
            value={growth.activeUsers24h}
            sub="users who posted a list"
            accent="text-sky-400"
          />
          <StatCard
            label="Active (7d)"
            value={growth.activeUsers7d}
            accent="text-sky-300"
          />
        </div>
        {growth.signupTrend.length > 0 && (
          <div className="mt-4 bg-gray-800 rounded-lg p-4 flex items-end gap-4">
            <div className="flex-1">
              <p className="text-xs text-gray-400 mb-2">
                Signup trend — last {growth.signupTrend.length} days
              </p>
              <MiniSparkline data={growth.signupTrend} />
            </div>
          </div>
        )}
      </div>

      {/* ── Engagement Row ─────────────────────────────────────────────── */}
      <div>
        <SectionHeader title="Engagement" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard label="Total Lists" value={engagement.totalLists} />
          <StatCard
            label="Lists Today"
            value={engagement.listsCreatedToday}
            accent="text-green-400"
          />
          <StatCard
            label="Lists (7d)"
            value={engagement.listsCreatedLast7Days}
            accent="text-green-300"
          />
          <StatCard
            label="Avg Lists / User"
            value={engagement.avgListsPerUser}
            accent="text-yellow-400"
          />
          <div className="bg-gray-800 rounded-lg px-4 py-5 space-y-3">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              User Depth
            </p>
            <PercentBar
              label="Have ≥1 list"
              count={engagement.usersWithList.count}
              percent={engagement.usersWithList.percent}
              color="bg-indigo-500"
            />
            <PercentBar
              label="Full Top 10"
              count={engagement.usersWithFullTop10.count}
              percent={engagement.usersWithFullTop10.percent}
              color="bg-purple-500"
            />
          </div>
        </div>
      </div>

      {/* ── Product Insights ─────────────────────────────────────────────── */}
      <div>
        <SectionHeader title="Product Insights" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase mb-3">
              Top Artists (appearances)
            </p>
            <ArtistTable rows={productInsights.topArtists} />
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase mb-3">
              Most Common #1 Picks
            </p>
            <ArtistTable rows={productInsights.topNumberOnePicks} />
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase mb-3">
              Trending (7d vs prior 7d)
            </p>
            <ArtistTable
              rows={productInsights.trendingArtists}
              showDelta={true}
            />
          </div>
        </div>
      </div>

      {/* ── Taste Insights ──────────────────────────────────────────────── */}
      {(tasteInsights.styles.length > 0 ||
        tasteInsights.eras.length > 0 ||
        tasteInsights.regions.length > 0) && (
        <div>
          <SectionHeader title="Taste Insights" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {/* Styles */}
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-400 uppercase mb-3">
                Top Styles
              </p>
              {tasteInsights.styles.length > 0 ? (
                <div className="space-y-2">
                  {tasteInsights.styles.map((s) => (
                    <PercentBar
                      key={s.key}
                      label={s.key}
                      count={s.count}
                      percent={s.percent}
                      color="bg-emerald-500"
                    />
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-xs">No style data</p>
              )}
            </div>

            {/* Eras */}
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-400 uppercase mb-3">
                Top Eras
              </p>
              {tasteInsights.eras.length > 0 ? (
                <div className="space-y-2">
                  {tasteInsights.eras.map((e) => (
                    <PercentBar
                      key={e.key}
                      label={e.key}
                      count={e.count}
                      percent={e.percent}
                      color="bg-amber-500"
                    />
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-xs">No era data</p>
              )}
            </div>

            {/* Regions */}
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-400 uppercase mb-3">
                Top Regions
              </p>
              {tasteInsights.regions.length > 0 ? (
                <div className="space-y-2">
                  {tasteInsights.regions.map((r) => (
                    <PercentBar
                      key={r.key}
                      label={r.key}
                      count={r.count}
                      percent={r.percent}
                      color="bg-rose-500"
                    />
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-xs">No region data</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Social & System ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Social */}
        <div className="bg-gray-800 rounded-lg p-4">
          <SectionHeader title="Social" />
          <div className="grid grid-cols-2 gap-4 mb-4">
            <StatCard label="Total Follows" value={social.totalFollows} />
            <StatCard
              label="Avg Follows / User"
              value={social.avgFollowsPerUser}
            />
          </div>
          {social.topUsers.length > 0 && (
            <>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
                Most Followed
              </p>
              <table className="w-full text-sm">
                <tbody>
                  {social.topUsers.map((u, i) => (
                    <tr
                      key={u.userId}
                      className="border-b border-gray-700 last:border-0"
                    >
                      <td className="py-1.5 w-6 text-gray-500 text-xs">
                        {i + 1}
                      </td>
                      <td className="py-1.5 text-gray-100">
                        <a
                          href={`/u/${u.username}`}
                          className="hover:text-sky-400 transition-colors"
                        >
                          {u.username}
                        </a>
                      </td>
                      <td className="py-1.5 text-right text-gray-400 text-xs">
                        {u.followerCount} followers
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {social.totalFollows === 0 && (
            <p className="text-gray-600 text-xs mt-2">No follow data yet</p>
          )}
        </div>

        {/* System */}
        <div className="bg-gray-800 rounded-lg p-4">
          <SectionHeader title="System Health" />
          <div className="grid grid-cols-2 gap-4">
            <StatCard
              label="Uptime"
              value={formatUptime(system.uptime)}
              accent="text-green-400"
            />
            <div className="bg-gray-700 rounded-lg px-4 py-5">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                Redis
              </p>
              <p
                className={`mt-1 text-2xl font-bold ${
                  system.redisStatus === "ok"
                    ? "text-green-400"
                    : system.redisStatus === "down"
                      ? "text-red-400"
                      : "text-yellow-400"
                }`}
              >
                {system.redisStatus === "ok"
                  ? "● OK"
                  : system.redisStatus === "down"
                    ? "● Down"
                    : "● Unknown"}
              </p>
            </div>
            <div className="bg-gray-700 rounded-lg px-4 py-5">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                API Latency
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-500">
                {system.apiAvgResponseTime != null
                  ? `${system.apiAvgResponseTime}ms`
                  : "—"}
              </p>
            </div>
            <div className="bg-gray-700 rounded-lg px-4 py-5">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                Error Rate (24h)
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-500">
                {system.errorRate != null ? `${system.errorRate}%` : "—"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardAdmin;
